import type { AuthConfig, AuthUtilities } from "@urql/exchange-auth"
import { authExchange } from "@urql/exchange-auth"

import { useAuthStore } from "@/stores/authStore"
import { exchangeTokenWithAPI, isTokenExpired, refreshTokenWithAPI } from "@/utils/auth"

const AUTH_ERROR_CODES = {
  UNAUTHORIZED: "UNAUTHORIZED",
  COGNITO_REFRESH_REQUIRED: "COGNITO_REFRESH_REQUIRED",
} as const

let isRefreshing = false
let refreshPromise: Promise<void> | null = null

export const urqlAuthExchange = authExchange(async (utilities: AuthUtilities): Promise<AuthConfig> => {
  return {
    addAuthToOperation(operation) {
      const { accessToken } = useAuthStore.getState().tokens

      if (!accessToken) {
        useAuthStore.getState().signIn()
        return {
          ...operation,
          context: {
            ...operation.context,
            skipOperation: true,
          },
        }
      }

      return utilities.appendHeaders(operation, {
        Authorization: `Bearer ${accessToken}`,
      })
    },

    didAuthError(error) {
      return error.graphQLErrors.some(
        (e) =>
          e.extensions?.code === AUTH_ERROR_CODES.UNAUTHORIZED ||
          e.message === AUTH_ERROR_CODES.COGNITO_REFRESH_REQUIRED
      )
    },

    willAuthError() {
      const { accessToken } = useAuthStore.getState().tokens
      return !accessToken || isTokenExpired(accessToken)
    },

    async refreshAuth(): Promise<void> {
      if (isRefreshing) {
        await refreshPromise
        return
      }

      isRefreshing = true
      refreshPromise = (async (): Promise<void> => {
        const store = useAuthStore.getState()
        const { refreshToken, idToken } = store.tokens

        try {
          let tokens
          if (refreshToken) {
            tokens = await refreshTokenWithAPI({
              accessToken: store.tokens.accessToken!,
              refreshToken,
            })
          } else if (idToken) {
            tokens = await exchangeTokenWithAPI(idToken)
          } else {
            store.signIn()
            return
          }

          store.setTokens(tokens)
        } catch (error: any) {
          console.error("Token refresh failed:", error)

          // Force sign-in to get fresh tokens
          await store.clearAuth()
          store.signIn()
        } finally {
          isRefreshing = false
          refreshPromise = null
        }
      })()

      return refreshPromise
    },
  }
})
